/**
 * Decodes the pre-filled box payload carried in /pages/recommendations?b=<...>, and
 * decides which delivery dates the page may offer for it.
 *
 * Pure. No DOM access -- safe to require() in Node tests and to load in the
 * browser (attaches to window.StockedBoxPayload).
 *
 * The payload is UNSIGNED by design: it holds variant IDs, quantities and
 * handles, no PII, and tampering only produces a cart the visitor could build by
 * hand. Every field is therefore validated here, because anything may arrive.
 *
 * The format is a contract with stocked-recommendations/src/encode.js, pinned by
 * test/fixtures/box-payload.json in both repos. Changing it breaks every link in
 * an already-sent email.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.StockedBoxPayload = api;
})(typeof self !== 'undefined' ? self : this, function (root) {
  // Resolved lazily: in the browser delivery-date-format.js is a separate deferred
  // script, so it must not be read at factory time.
  function fmt() {
    if (root && root.StockedDeliveryFormat) return root.StockedDeliveryFormat;
    return require('./delivery-date-format.js');
  }

  function b64urlDecode(str) {
    var padded = str.replace(/-/g, '+').replace(/_/g, '/');
    padded += '='.repeat((4 - (padded.length % 4)) % 4);
    if (typeof atob === 'function') {
      var bin = atob(padded);
      var bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new TextDecoder().decode(bytes);
    }
    return Buffer.from(padded, 'base64').toString('utf8');
  }

  function isPositiveInt(n) {
    return Number.isInteger(n) && n > 0;
  }

  // Returns { v, d, w: { weekCode: [[variantId, quantity, handle], ...] }, s: [variantId] }
  // or null for anything missing or malformed.
  function decodeBox(str) {
    if (typeof str !== 'string' || str === '') return null;

    var payload;
    try {
      payload = JSON.parse(b64urlDecode(str));
    } catch (e) {
      return null;
    }

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    if (payload.v !== 1) return null;
    if (typeof payload.d !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(payload.d)) return null;
    if (!payload.w || typeof payload.w !== 'object' || Array.isArray(payload.w)) return null;
    if (!Array.isArray(payload.s) || !payload.s.every(isPositiveInt)) return null;

    var codes = Object.keys(payload.w);
    if (codes.length === 0) return null;

    for (var i = 0; i < codes.length; i++) {
      if (!fmt().parseWeekCode(codes[i])) return null;
      var items = payload.w[codes[i]];
      if (!Array.isArray(items) || items.length === 0) return null;
      for (var j = 0; j < items.length; j++) {
        var line = items[j];
        if (!Array.isArray(line) || line.length !== 3) return null;
        if (!isPositiveInt(line[0]) || !isPositiveInt(line[1])) return null;
        if (typeof line[2] !== 'string' || !/^[a-z0-9-]+$/.test(line[2])) return null;
      }
    }

    // The default date must land in one of the weeks carried, or the page has
    // nothing to render on load.
    var defaultWeek = fmt().weekCodeOf(payload.d);
    if (!defaultWeek || codes.indexOf(defaultWeek) === -1) return null;

    return payload;
  }

  // selectableDates() entries whose week has a box in the payload. Dates in other
  // weeks are omitted entirely rather than offered and then refused (spec §6.5).
  function offerableDates(payload, selectable) {
    if (!Array.isArray(selectable)) return [];
    return selectable.filter(function (entry) {
      var code = fmt().weekCodeOf(entry.iso);
      return !!code && Object.prototype.hasOwnProperty.call(payload.w, code);
    });
  }

  // The date the page opens on. The payload default is computed the night the
  // email is sent, so by the time the link is opened it may be past its cutoff:
  // fall back to another date in the same week (same box), then to the earliest
  // offerable date. null means every week in the link has closed.
  function initialDate(payload, offerable) {
    if (!offerable.length) return null;
    var exact = offerable.find(function (e) { return e.iso === payload.d; });
    if (exact) return exact.iso;
    var week = fmt().weekCodeOf(payload.d);
    var sameWeek = offerable.find(function (e) { return fmt().weekCodeOf(e.iso) === week; });
    return (sameWeek || offerable[0]).iso;
  }

  return { decodeBox: decodeBox, offerableDates: offerableDates, initialDate: initialDate };
});
